import { Matrix4, type Vector3 } from 'three';
import type { SideId } from '@/domain/anatomy/side';
import { positionalPart } from '@/domain/geometry/matrix';
import { vector3 } from '@/domain/geometry/vector3';
import type {
    FittedStem,
    FittedStemAxes,
    FittedStemAxis,
} from '@/views/3d-planner/fitting/fittedStem';
import { degToRad } from '@/util/math';
import { logger } from '@/util/logger';
import { formatDegrees, formatDistance } from '@/domain/geometry/formatMath';
import { matricesApproxEqual } from '@/domain/geometry/approxEquals';

const log = logger();

const IDENTITY = new Matrix4();

/**
 * Whether the stem has been moved off its fitted placement.
 *
 * Approximate, and on the same default tolerance `templateComparison` compares a stem transform
 * with: a rotation applied and then undone composes to within 1e-16 of the identity rather than
 * onto it, so an exact comparison reads as a manual placement that the approve and reset buttons
 * have already forgotten. Every control asking this has to answer the same way.
 */
export function hasStemAdjustment(stemTransform: Matrix4): boolean {
    return !matricesApproxEqual(stemTransform, IDENTITY);
}

/**
 * A representation of a **change** in the transform of a stem relative to a femur.
 *
 * **Note** the rotation numbers are interpreted relative to the current transform of the stem, as they
 * take place around various axes. They cannot be simply mapped to changes to the rotation of the
 * stem.
 * */
export type StemTransformChange = {
    /** posterior-anterior translation */
    translatePA: number;
    /** inferior-superior translation */
    translateIS: number;
    /** medial-lateral translation */
    translateML: number;
    /** retroversion-anteversion rotation */
    rotateRA: number;
    /** extension-flexion rotation */
    rotateEF: number;
    /** valgus-varus rotation */
    rotateVV: number;
};

export function applyStemTranslation(
    stemTransform: Matrix4,
    change: Partial<StemTransformChange>,
    operationalSide: SideId,
): void {
    const [lateral, anterior, superior] = [
        change.translateML ?? 0,
        change.translatePA ?? 0,
        change.translateIS ?? 0,
    ];

    // In a left-side case the medial-to-lateral direction is to the left
    const left = operationalSide === 'left' ? lateral : -lateral;

    stemTransform.setPosition(
        positionalPart(stemTransform).add(vector3(left, -anterior, superior)),
    );
}

export function applyStemRotation(
    stemTransform: Matrix4,
    change: Partial<StemTransformChange>,
    operationalSide: SideId,
    fittedStemAxes: FittedStemAxes,
): void {
    const [ef, ra, vv] = [change.rotateEF ?? 0, change.rotateRA ?? 0, change.rotateVV ?? 0];

    // The rotation angles depend on whether this is a left or right-side case
    const [neckShaftRotation, stemShaftRotation, paRotation] =
        operationalSide === 'left' ? [-ef, ra, -vv] : [ef, -ra, vv];
    applyRotation(stemTransform, neckShaftRotation, fittedStemAxes.neckAxis);
    applyRotation(stemTransform, stemShaftRotation, fittedStemAxes.shaftAxis);
    applyRotation(stemTransform, paRotation, fittedStemAxes.paAxis);
}

function applyRotation(stemTransform: Matrix4, angleDegrees: number, axis: FittedStemAxis): void {
    if (angleDegrees == 0) {
        // If the angle is zero there is no rotation to do
        return;
    }
    const translation = new Matrix4().makeTranslation(axis.position);
    const rotation = new Matrix4().makeRotationAxis(axis.direction, degToRad(angleDegrees));
    stemTransform.multiply(translation).multiply(rotation).multiply(translation.invert());
}

/**
 * Anchor point for the resection-alignment transform when switching stems.
 * Selects which landmark is held fixed in world space while the calcar plane orientation
 * is preserved.
 *
 * - `head-centre`     — stem-group origin (= fitted head centre). Used for size changes.
 * - `resection-origin` — origin of the resection plane. Used for neck-offset changes.
 */
export type StemSwitchAnchor = 'head-centre' | 'resection-origin';

/**
 * Compute a new `stemTransform` that carries over a manual adjustment from one stem to another
 * by aligning resection (calcar) planes and preserving a chosen anchor landmark.
 *
 * The rotational part aligns the new stem's resection plane to match the old stem's
 * manually-adjusted resection plane. The translational part anchors the new stem so that
 * the chosen landmark coincides between old and new stems in world space.
 */
export function computeStemTransformFromResectionAlignment(
    oldFitted: FittedStem,
    oldStemTransform: Matrix4,
    newFitted: FittedStem,
    anchor: StemSwitchAnchor,
): Matrix4 {
    // Effective world-space transform of the old stem group (auto-fit + manual offset)
    const T_old_eff = oldFitted.transform.clone().multiply(oldStemTransform);

    // Old stem's resection frame expressed in world space
    const oldNormal = oldFitted.resectionPlaneLocal.normal.clone().transformDirection(T_old_eff);
    const oldX = oldFitted.resectionPlaneLocal.x.clone().transformDirection(T_old_eff);
    const oldY = oldFitted.resectionPlaneLocal.y.clone().transformDirection(T_old_eff);

    // Anchor point in world space (old) and stem-group local space (new)
    const oldAnchor = anchorPointLocal(oldFitted, anchor).applyMatrix4(T_old_eff);
    const newAnchor = anchorPointLocal(newFitted, anchor);

    // F_old: resection frame + anchor in world space
    // prettier-ignore
    const F_old = new Matrix4().set(
        oldX.x, oldY.x, oldNormal.x, oldAnchor.x,
        oldX.y, oldY.y, oldNormal.y, oldAnchor.y,
        oldX.z, oldY.z, oldNormal.z, oldAnchor.z,
        0, 0, 0, 1,
    );

    // F_new: resection frame + anchor in stem-group space
    const newNormal = newFitted.resectionPlaneLocal.normal;
    const newX = newFitted.resectionPlaneLocal.x;
    const newY = newFitted.resectionPlaneLocal.y;
    // prettier-ignore
    const F_new = new Matrix4().set(
        newX.x, newY.x, newNormal.x, newAnchor.x,
        newX.y, newY.y, newNormal.y, newAnchor.y,
        newX.z, newY.z, newNormal.z, newAnchor.z,
        0, 0, 0, 1,
    );

    // Effective new transform: places the new stem's resection frame onto the old stem's
    // world-space resection frame, anchored at the chosen landmark.
    const T_new_eff = F_old.multiply(F_new.invert());

    // Express as a manual offset relative to the new stem's auto-fit.
    return newFitted.transform.clone().invert().multiply(T_new_eff);
}

function anchorPointLocal(fitted: FittedStem, anchor: StemSwitchAnchor): Vector3 {
    switch (anchor) {
        case 'head-centre':
            return positionalPart(fitted.headLocal);
        case 'resection-origin':
            return fitted.resectionPlaneLocal.origin.clone();
    }
}

export function logStemTransformChange(change: Partial<StemTransformChange>) {
    const logTranslation = (
        key: keyof StemTransformChange,
        negativeName: string,
        positiveName: string,
    ) => {
        const value = change[key];
        if (value) {
            log.info(
                'Translating stem in the %s direction by %s',
                value > 0 ? positiveName : negativeName,
                formatDistance(Math.abs(value)),
            );
        }
    };
    const logRotation = (
        key: keyof StemTransformChange,
        negativeName: string,
        positiveName: string,
    ) => {
        const value = change[key];
        if (value) {
            log.info(
                'Rotating stem in the %s direction by %s',
                value > 0 ? positiveName : negativeName,
                formatDegrees(Math.abs(value)),
            );
        }
    };
    logTranslation('translatePA', 'anterior', 'posterior');
    logTranslation('translateIS', 'inferior', 'superior');
    logTranslation('translateML', 'medial', 'lateral');
    logRotation('rotateRA', 'retroversion', 'anteversion');
    logRotation('rotateEF', 'extension', 'flexion');
    logRotation('rotateVV', 'valgus', 'varus');
}
